'use strict';

/**
 * NACRE Wave 2.1.3 — seed primitive (BIP39 + BIP32)
 *
 * Three operations needed by the vault system:
 *
 *   1. generateMnemonic() — produces a fresh 12-word BIP39 English mnemonic
 *      using @scure/bip39's CSPRNG-backed generator. Used at signup.
 *
 *   2. mnemonicToSeed(mnemonic) — turns a 12-word phrase into a 64-byte
 *      seed via PBKDF2-HMAC-SHA512 per BIP39. Used during signup AND
 *      during recovery to re-derive the same seed bytes.
 *
 *   3. deriveWalletKey(seed, path) — runs BIP32 derivation along the
 *      given path and returns {publicKey, privateKey} for the leaf.
 *      Used for the wallet identity and (later) for HKDF subkey
 *      derivation rooted in the seed.
 *
 * Derivation path: m/44'/8453'/0'/0/0
 *   - 44' = BIP44 (purpose)
 *   - 8453' = SLIP-0044 coin type for Base mainnet
 *   - 0' = account index (first account)
 *   - 0 = external chain (receiving addresses)
 *   - 0 = first address index
 *
 * The mnemonic is the SECRET. It MUST never be sent to the server. The
 * server only sees the public key derived from the wallet path, plus
 * other public keys derived from the seed via the hybrid-kex / hybrid-sign
 * primitives.
 *
 * Backup story: the user writes down the 12 words at signup. With the
 * 12 words, every key in every vault can be regenerated identically on
 * any device. Without the 12 words, recovery falls back to the user's
 * password (the only other unwrap path).
 */

const bip39 = require('@scure/bip39');
const { wordlist: englishWordlist } = require('@scure/bip39/wordlists/english.js');
const { HDKey } = require('@scure/bip32');

const BASE_DERIVATION_PATH = "m/44'/8453'/0'/0/0";

/**
 * Generate a fresh 12-word English BIP39 mnemonic.
 *
 * 128 bits of entropy + 4 bits of checksum = 132 bits → 12 words.
 * (160 bits → 15 words, 192 → 18, 224 → 21, 256 → 24.)
 *
 * @returns {string} space-separated 12-word English mnemonic
 */
function generateMnemonic() {
  return bip39.generateMnemonic(englishWordlist, 128);
}

/**
 * Validate a candidate mnemonic against the BIP39 checksum + English wordlist.
 *
 * @param {string} mnemonic
 * @returns {boolean}
 */
function validateMnemonic(mnemonic) {
  if (typeof mnemonic !== 'string' || mnemonic.length === 0) return false;
  try {
    return bip39.validateMnemonic(mnemonic, englishWordlist);
  } catch (_e) {
    return false;
  }
}

/**
 * Convert a mnemonic to its 64-byte BIP39 seed (PBKDF2-HMAC-SHA512).
 *
 * @param {string} mnemonic
 * @param {string} [passphrase=''] — optional BIP39 passphrase ("25th word")
 * @returns {Uint8Array} 64 bytes
 * @throws if the mnemonic is invalid
 */
function mnemonicToSeed(mnemonic, passphrase) {
  if (!validateMnemonic(mnemonic)) {
    throw new Error('Invalid mnemonic: failed BIP39 validation');
  }
  return bip39.mnemonicToSeedSync(mnemonic, passphrase || '');
}

/**
 * Derive a wallet keypair at the given BIP32 path from a 64-byte seed.
 *
 * @param {Uint8Array} seed — 64-byte seed (from mnemonicToSeed)
 * @param {string} path — BIP32 derivation path, e.g. "m/44'/8453'/0'/0/0"
 * @returns {{publicKey: Uint8Array, privateKey: Uint8Array}}
 * @throws if seed or path is invalid
 */
function deriveWalletKey(seed, path) {
  if (!(seed instanceof Uint8Array) || seed.length === 0) {
    throw new Error('Invalid seed: must be non-empty Uint8Array');
  }
  if (typeof path !== 'string' || !path.startsWith('m/')) {
    throw new Error('Invalid path: must be a BIP32 derivation path starting with "m/"');
  }
  const root = HDKey.fromMasterSeed(seed);
  const node = root.derive(path);
  if (!node.publicKey || !node.privateKey) {
    throw new Error('Derivation produced an incomplete key (path may be unhardened public-only)');
  }
  return {
    publicKey: new Uint8Array(node.publicKey),
    privateKey: new Uint8Array(node.privateKey),
  };
}

module.exports = {
  generateMnemonic,
  validateMnemonic,
  mnemonicToSeed,
  deriveWalletKey,
  BASE_DERIVATION_PATH,
};
